import { hashStringToSeed, mulberry32 } from './seededRandom';
import { cornerPillarOffsets } from './buildingGeometry';

/** Vertical wall rib x-offsets along front/back face width. */
export function wallRibOffsets(wallW, count = 5) {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const margin = Math.min(0.35, wallW * 0.12);
  const usable = wallW - margin * 2;
  const step = usable / (count - 1);
  return Array.from({ length: count }, (_, i) => -usable / 2 + step * i);
}

/**
 * Multi-pane layout for front windows.
 * @param {number[]} windowXs - center x of each window
 * @param {number} winW
 * @param {number} winH
 * @returns {{dx:number,dy:number,w:number,h:number}[]}
 */
export function windowPaneLayout(windowXs, winW, winH) {
  const gap = 0.04;
  const pw = (winW - gap) / 2;
  const ph = (winH - gap) / 2;
  return windowXs.flatMap((wx) => {
    const out = [];
    for (const ox of [-1, 1]) {
      for (const oy of [-1, 1]) {
        out.push({
          dx: wx + ox * (pw + gap) * 0.5,
          dy: oy * (ph + gap) * 0.5,
          w: pw,
          h: ph,
        });
      }
    }
    return out;
  });
}

const CLUTTER_KINDS = ['hvac', 'vent', 'antenna', 'pipe'];

/**
 * Seeded rooftop clutter for a room. 2–4 items.
 * @returns {{kind:string,x:number,z:number,rotY:number,scale:number}[]}
 */
export function roofClutterItems(roomId, boxW, boxD) {
  const rng = mulberry32(hashStringToSeed(`roof:${roomId}`));
  const count = 2 + Math.floor(rng() * 3); // 2..4
  const items = [];
  for (let i = 0; i < count; i++) {
    const kind = CLUTTER_KINDS[Math.floor(rng() * CLUTTER_KINDS.length)];
    const x = (rng() - 0.5) * boxW * 0.55;
    const z = (rng() - 0.5) * boxD * 0.55;
    items.push({
      kind,
      x,
      z,
      rotY: rng() * Math.PI * 2,
      scale: 0.85 + rng() * 0.35,
    });
  }
  return items;
}

/** Mullion bars for greenhouse glass shell (local coords relative to glass center). */
export function mullionLayout(boxW, glassH, boxD, vDiv = 2, hDiv = 2) {
  const bars = { vertical: [], horizontal: [] };
  for (let i = 1; i <= vDiv; i++) {
    const x = (i / (vDiv + 1) - 0.5) * boxW;
    bars.vertical.push({ face: 'front', x, y: 0 });
    bars.vertical.push({ face: 'back', x, y: 0 });
  }
  for (let i = 1; i <= vDiv; i++) {
    const z = (i / (vDiv + 1) - 0.5) * boxD;
    bars.vertical.push({ face: 'left', z, y: 0 });
    bars.vertical.push({ face: 'right', z, y: 0 });
  }
  for (let i = 1; i <= hDiv; i++) {
    const y = (i / (hDiv + 1) - 0.5) * glassH;
    bars.horizontal.push({ face: 'front', y });
    bars.horizontal.push({ face: 'back', y });
    bars.horizontal.push({ face: 'left', y });
    bars.horizontal.push({ face: 'right', y });
  }
  return bars;
}

/** Interior plant-bed slabs (silhouette only). */
export function plantBedLayout(boxW, boxD, count = 3) {
  const beds = [];
  const bedW = boxW * 0.22;
  const bedD = boxD * 0.7;
  const span = boxW * 0.7;
  const start = -span / 2;
  const step = count === 1 ? 0 : span / (count - 1);
  for (let i = 0; i < count; i++) {
    beds.push({
      x: count === 1 ? 0 : start + step * i,
      z: 0,
      w: bedW,
      d: bedD,
      h: 0.18,
    });
  }
  return beds;
}

export function scaffoldUprights(boxW, boxD) {
  return cornerPillarOffsets(boxW, boxD, 0.12);
}

export function ghostUprights(boxW, boxD) {
  return cornerPillarOffsets(boxW, boxD, 0.08);
}

/**
 * Dashed ring segments on XZ plane.
 * @param {number} radius
 * @param {number} segments - total ring divisions; returns every other as a dash
 */
export function dashedRingPoints(radius, segments = 16) {
  const dashes = [];
  const dashAngle = (Math.PI * 2) / segments;
  for (let i = 0; i < segments; i += 2) {
    const a = i * dashAngle;
    dashes.push({
      x: Math.cos(a) * radius,
      z: Math.sin(a) * radius,
      rotationY: -a + Math.PI / 2,
      length: radius * dashAngle * 0.85,
    });
  }
  return dashes;
}
